/**
 * financialStatusStore - Dedicated store for financial status with proper caching
 * 
 * CENTRALIZED RPC LAYER - All get_or_create_financial_status_with_session calls MUST go through here.
 * 
 * GUARANTEES:
 * - WINDOW-LEVEL GLOBAL LOCK: Uses window.__fw_fin_status to ensure singleton across module instances
 * - Single-flight dedupe: concurrent requests for same key reuse the same Promise
 * - Mount cooldown (10s): mount-reason calls within 10s of last success return cached result immediately
 * - Event reasons (force=true) bypass cooldown but still dedupe in-flight
 */
import { supabase } from '@/integrations/supabase/client';
import { SessionManager } from '@/services/sessionManager';
import { log, logError } from '@/lib/log';

export interface FinancialStatusData {
  current_money: number;
  total_earned: number;
  total_spent: number;
  current_time_period_id: string | null;
}

// Mount cooldown: 10 seconds (increased for safety)
const MOUNT_COOLDOWN_MS = 10000;

// ============= WINDOW-LEVEL GLOBAL STATE =============
// This ensures the state is a true singleton even if the module is loaded multiple times

interface WindowFinStatusState {
  inFlight: Map<string, Promise<FinancialStatusData | null>>;
  lastResult: Map<string, FinancialStatusData>;
  lastAt: Map<string, number>;
  currentPlayerId: string | null;
}

declare global {
  interface Window {
    __fw_fin_status?: WindowFinStatusState;
  }
}

/**
 * Get or initialize the global state on window
 */
function getGlobalState(): WindowFinStatusState {
  if (typeof window === 'undefined') {
    // SSR fallback - return ephemeral state
    return {
      inFlight: new Map(),
      lastResult: new Map(),
      lastAt: new Map(),
      currentPlayerId: null
    };
  }
  
  if (!window.__fw_fin_status) {
    window.__fw_fin_status = {
      inFlight: new Map(),
      lastResult: new Map(),
      lastAt: new Map(),
      currentPlayerId: null
    };
    
    if (import.meta.env.DEV) {
      console.log('[financialStatusStore] Initialized window.__fw_fin_status (global singleton)');
    }
  }
  
  return window.__fw_fin_status;
}

/**
 * Generate a unique key for request deduplication
 */
function getRequestKey(playerId: string, sessionToken: string): string {
  return `${playerId}:${sessionToken}`;
}

/**
 * Check if this is a mount-type reason (should be rate-limited)
 */
function isMountReason(reason: string): boolean {
  return reason.includes('mount') || reason.includes('initial') || reason.includes('bootstrap');
}

/**
 * Load financial status with strict mount cooldown
 * 
 * BEHAVIOR:
 * 1. If there's an inFlight request for the same key → return it (no new RPC)
 * 2. If reason is "mount" AND lastAt[key] exists AND (now - lastAt[key]) < 10000ms → return lastResult[key] immediately
 * 3. Otherwise, perform RPC call, store Promise in inFlight BEFORE awaiting, then update lastAt[key] and lastResult[key]
 * 
 * Event reasons (force=true) bypass the cooldown but still dedupe in-flight.
 */
export async function loadFinancialStatus(options: {
  playerId: string;
  sessionToken: string;
  reason?: string;
  force?: boolean;
}): Promise<FinancialStatusData | null> {
  const { playerId, sessionToken, reason = 'unknown', force = false } = options;
  const requestKey = getRequestKey(playerId, sessionToken);
  const isMount = isMountReason(reason);
  const now = Date.now();
  
  // Get global state from window
  const state = getGlobalState();

  // DEV: Log every call with diagnostic info and emit event for DevRequestPanel
  if (import.meta.env.DEV) {
    const lastSuccess = state.lastAt.get(requestKey);
    const cooldownRemaining = lastSuccess ? Math.max(0, MOUNT_COOLDOWN_MS - (now - lastSuccess)) : 0;
    const hasCached = state.lastResult.has(requestKey);
    const hasInFlight = state.inFlight.has(requestKey);
    
    // Emit diagnostic event for DevRequestPanel
    window.dispatchEvent(new CustomEvent('fw:financial-status-call', { 
      detail: {
        reason,
        force,
        isMount,
        hasCached,
        hasInFlight,
        cooldownRemaining,
        willFetch: !hasInFlight && (!hasCached || force || !isMount || cooldownRemaining <= 0),
        timestamp: now
      }
    }));
    
    console.groupCollapsed(`[financialStatusStore] loadFinancialStatus`);
    console.log('reason:', reason, '| force:', force, '| isMount:', isMount);
    console.log('requestKey:', requestKey);
    console.log('inFlight:', hasInFlight);
    console.log('lastAt:', lastSuccess ? `${now - lastSuccess}ms ago` : 'never');
    console.log('cooldownRemaining:', cooldownRemaining, 'ms');
    console.log('hasCachedResult:', hasCached);
    console.log('willFetch:', !hasInFlight && (!hasCached || force || !isMount || cooldownRemaining <= 0));
    console.trace('callsite');
    console.groupEnd();
  }

  if (!playerId || !sessionToken) {
    log('[financialStatus] missing playerId or sessionToken, skipping');
    return null;
  }

  // If player changed (real switch, not token refresh), clear all per-key state
  if (state.currentPlayerId && state.currentPlayerId !== playerId) {
    log('[financialStatus] player changed, clearing all per-key state');
    state.inFlight.clear();
    state.lastAt.clear();
    state.lastResult.clear();
  }
  state.currentPlayerId = playerId;

  // GUARD 1: Single-flight dedupe - return existing in-flight request
  const existingInFlight = state.inFlight.get(requestKey);
  if (existingInFlight) {
    log('[financialStatus] returning existing in-flight promise', { reason, requestKey });
    return existingInFlight;
  }

  // GUARD 2: Mount cooldown - if mount reason and within cooldown, return cached result
  if (!force && isMount) {
    const lastSuccess = state.lastAt.get(requestKey);
    const cachedResult = state.lastResult.get(requestKey);
    
    if (lastSuccess && cachedResult && (now - lastSuccess) < MOUNT_COOLDOWN_MS) {
      log('[financialStatus] mount cooldown: returning cached result immediately', { 
        reason, 
        ageMs: now - lastSuccess,
        cooldownRemaining: MOUNT_COOLDOWN_MS - (now - lastSuccess)
      });
      return cachedResult;
    }
  }

  // Perform the actual RPC call
  const start = Date.now();
  log('[financialStatus] starting RPC fetch', { reason, requestKey, force, isMount });
  
  // CRITICAL: Create and store promise ATOMICALLY to prevent race condition
  // The promise is stored in the map BEFORE any async work starts
  let resolvePromise: (value: FinancialStatusData | null) => void;
  
  const fetchPromise = new Promise<FinancialStatusData | null>((resolve) => {
    resolvePromise = resolve;
  });
  
  // Store in in-flight map IMMEDIATELY (before any async work)
  state.inFlight.set(requestKey, fetchPromise);
  
  // Now perform the async work
  (async () => {
    try {
      const result = await SessionManager.withSessionRetry(async (session) => {
        return supabase.rpc('get_or_create_financial_status_with_session', {
          p_session_token: session.sessionToken,
          p_player_id: playerId
        });
      });

      if (result.error) {
        logError('[financialStatus] RPC error:', result.error);
        resolvePromise!(null);
        return;
      }

      const rawData = result.data as Record<string, unknown> | null;
      if (!rawData) {
        log('[financialStatus] No data returned from RPC');
        resolvePromise!(null);
        return;
      }

      const data: FinancialStatusData = {
        current_money: (rawData.current_money as number) ?? 0,
        total_earned: (rawData.total_earned as number) ?? 0,
        total_spent: (rawData.total_spent as number) ?? 0,
        current_time_period_id: (rawData.current_time_period_id as string) ?? null
      };

      log('[financialStatus] fetch completed successfully', { 
        ms: Date.now() - start, 
        reason,
        current_money: data.current_money
      });

      // Update per-key caches in global state
      state.lastAt.set(requestKey, Date.now());
      state.lastResult.set(requestKey, data);

      resolvePromise!(data);
    } catch (err) {
      logError('[financialStatus] Exception:', err);
      resolvePromise!(null);
    } finally {
      // Remove from in-flight map
      state.inFlight.delete(requestKey);
    }
  })();

  return fetchPromise;
}

/**
 * Get cached financial status without triggering a fetch
 * Returns the most recent successful result for any key of the current player
 */
export function getCachedFinancialStatus(): FinancialStatusData | null {
  const state = getGlobalState();
  
  // Return any cached result for current player
  for (const [key, result] of state.lastResult.entries()) {
    if (state.currentPlayerId && key.startsWith(state.currentPlayerId)) {
      return result;
    }
  }
  return null;
}

/**
 * Update cached financial status from external source (e.g., after money award)
 * This updates the cache immediately without needing a network request
 */
export function updateCachedFinancialStatus(update: Partial<FinancialStatusData>, source?: string): void {
  const state = getGlobalState();
  
  // Update all cached results for current player
  for (const [key, result] of state.lastResult.entries()) {
    if (state.currentPlayerId && key.startsWith(state.currentPlayerId)) {
      const prevMoney = result.current_money;
      const newMoney = update.current_money ?? prevMoney;
      const delta = newMoney - prevMoney;
      
      // DIAGNOSTIC LOG: Track all money changes
      console.log(`💰 [MONEY CHANGE] source=${source ?? 'unknown'} | prev=${prevMoney} | new=${newMoney} | delta=${delta > 0 ? '+' : ''}${delta}`);
      
      if (import.meta.env.DEV) {
        console.trace('[MONEY CHANGE] Call stack');
      }
      
      state.lastResult.set(key, { ...result, ...update });
      log('[financialStatus] cache updated from external source', { ...update, source, delta });
    }
  }
}

/**
 * Clear the financial status cache and reset all guards
 * Use on logout or full reset
 */
export function clearFinancialStatusCache(): void {
  const state = getGlobalState();
  state.inFlight.clear();
  state.lastAt.clear();
  state.lastResult.clear();
  state.currentPlayerId = null;
  log('[financialStatus] all caches cleared');
}

/**
 * Reset the fetch guard for a specific player (allows next mount to fetch)
 * Use this when you know the data has changed and want to allow a fresh fetch
 */
export function resetFetchGuard(playerId?: string): void {
  const state = getGlobalState();
  
  if (playerId) {
    // Remove all entries for this player
    for (const key of state.lastAt.keys()) {
      if (key.startsWith(playerId)) {
        state.lastAt.delete(key);
      }
    }
  } else {
    state.lastAt.clear();
  }
  log('[financialStatus] fetch guard reset', { playerId: playerId ?? 'all' });
}

/**
 * Force refresh financial status - bypasses ALL guards (cooldown, in-flight dedupe)
 * Use on FreeWorld entry or after money-changing operations
 */
export async function forceRefreshFinancialStatus(options: {
  playerId: string;
  sessionToken: string;
  reason?: string;
}): Promise<FinancialStatusData | null> {
  const { playerId, sessionToken, reason = 'force-refresh' } = options;
  const state = getGlobalState();
  const requestKey = getRequestKey(playerId, sessionToken);
  
  // DEV: Log with special marker
  if (import.meta.env.DEV) {
    console.count('fw:wallet_fetch');
    console.log('[financialStatusStore] 🔄 FORCE REFRESH', { reason, playerId: playerId.slice(-6) });
  }
  
  // Clear all guards for this key
  state.lastAt.delete(requestKey);
  state.inFlight.delete(requestKey);
  
  // Perform fresh fetch with force=true
  return loadFinancialStatus({
    playerId,
    sessionToken,
    reason,
    force: true
  });
}

/**
 * Get cache stats for debugging
 */
export function getFinancialStatusCacheStats(): { 
  inFlightCount: number;
  cachedResultCount: number;
  oldestCacheAgeMs: number | null;
} {
  const state = getGlobalState();
  let oldestAge: number | null = null;
  const now = Date.now();
  
  for (const timestamp of state.lastAt.values()) {
    const age = now - timestamp;
    if (oldestAge === null || age > oldestAge) {
      oldestAge = age;
    }
  }
  
  return {
    inFlightCount: state.inFlight.size,
    cachedResultCount: state.lastResult.size,
    oldestCacheAgeMs: oldestAge
  };
}
